import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Bell, Search, Download, ChevronLeft, ChevronRight,
  AlertTriangle, Car, Send, RefreshCw, Filter, X, User
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification type labels and colors
const NOTIFICATION_TYPES = {
  normalCalls: { label: "Normal Call", color: "bg-slate-600" },
  priorityCalls: { label: "Priority Call", color: "bg-amber-600" },
  callUpdates: { label: "Call Update", color: "bg-blue-600" },
  priorityCallUpdates: { label: "Priority Update", color: "bg-amber-500" },
  transmitted1085: { label: "10-85", color: "bg-orange-600" },
  transmitted1013: { label: "10-13", color: "bg-red-600" },
  transmitted1085Updates: { label: "10-85 Update", color: "bg-orange-500" },
  transmitted1013Updates: { label: "10-13 Update", color: "bg-red-500" },
  patrolActivity: { label: "Patrol", color: "bg-emerald-600" },
  masterNotification: { label: "Master", color: "bg-purple-600" },
  perpetratorUpdates: { label: "Profile", color: "bg-violet-600" }
};

export default function NotificationHistory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Filters (admin only for most)
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const loadNotifications = async (pageNum = 1) => {
    setLoading(true);
    try {
      let url = `${API}/user-notifications?page=${pageNum}&limit=50`;

      if (isAdmin) {
        if (startDate) url += `&start_date=${startDate}`;
        if (endDate) url += `&end_date=${endDate}`;
      }
      if (filterType) url += `&notification_type=${filterType}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await axios.get(url, { withCredentials: true });
      setNotifications(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      toast.error("Failed to load notification history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleSearch = () => {
    loadNotifications(1);
  };

  const clearFilters = () => {
    setSearch("");
    setFilterType("");
    setStartDate("");
    setEndDate("");
    loadNotifications(1);
  };

  const exportCSV = async () => {
    try {
      let url = `${API}/user-notifications/export?`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      if (filterType) url += `&notification_type=${filterType}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await axios.get(url, { withCredentials: true });

      if (response.data.items.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create CSV content
      const headers = [
        "Date/Time",
        "Type",
        "Title",
        "Message",
        "Call Number",
        "Location",
        "Incident Type",
        "Priority",
        "10-85",
        "10-13",
        "Update",
        "Patrol Car",
        "Patrol Action",
        "Sent By"
      ];

      const rows = response.data.items.map(n => [
        `"${new Date(n.received_at).toLocaleString("en-US", { timeZone: "America/New_York" })}"`,
        NOTIFICATION_TYPES[n.notification_type]?.label || n.notification_type,
        `"${(n.title || "").replace(/"/g, '""')}"`,
        `"${(n.message || "").replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        n.call_number || "",
        `"${(n.location || "").replace(/"/g, '""')}"`,
        n.incident_type || "",
        n.is_priority ? "Yes" : "No",
        n.has_1085 ? "Yes" : "No",
        n.has_1013 ? "Yes" : "No",
        n.is_update ? "Yes" : "No",
        n.patrol_car_number || "",
        n.patrol_action || "",
        n.sent_by_name || ""
      ]);

      const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `notification_history_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success("CSV exported");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export");
    }
  };

  const getTypeInfo = (type) => {
    return NOTIFICATION_TYPES[type] || { label: type, color: "bg-slate-500" };
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6 text-blue-500" />
              <CardTitle className="text-xl uppercase tracking-wider">
                Notification History
              </CardTitle>
              <Badge variant="outline" className="ml-2">
                {total} total
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadNotifications(page)}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters
              </Button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-400 border-green-400/50"
                  onClick={exportCSV}
                >
                  <Download className="w-4 h-4 mr-1" />
                  CSV
                </Button>
              )}
            </div>
          </div>

          {!isAdmin && (
            <p className="text-sm text-slate-400 mt-2">
              Showing notifications from the last 24 hours
            </p>
          )}
        </CardHeader>

        <CardContent>
          {/* Filters */}
          {showFilters && (
            <div className="mb-4 p-3 bg-slate-800 rounded-lg border border-slate-700">
              <div className="flex flex-wrap gap-3 items-end">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs text-slate-400 block mb-1">Search</label>
                  <Input
                    placeholder="Search title, message, location..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-dark h-9"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>

                {/* Type filter */}
                <div className="w-40">
                  <label className="text-xs text-slate-400 block mb-1">Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white h-9"
                  >
                    <option value="">All Types</option>
                    {Object.entries(NOTIFICATION_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>

                {/* Date filters (admin only) */}
                {isAdmin && (
                  <>
                    <div className="w-36">
                      <label className="text-xs text-slate-400 block mb-1">From</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white h-9"
                      />
                    </div>
                    <div className="w-36">
                      <label className="text-xs text-slate-400 block mb-1">To</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white h-9"
                      />
                    </div>
                  </>
                )}

                {/* Action buttons */}
                <Button size="sm" onClick={handleSearch} className="h-9">
                  <Search className="w-4 h-4 mr-1" />
                  Search
                </Button>
                <Button size="sm" variant="ghost" onClick={clearFilters} className="h-9 text-slate-400">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Notifications list */}
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No notifications found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notif) => {
                const typeInfo = getTypeInfo(notif.notification_type);
                return (
                  <div
                    key={notif.record_id}
                    className="p-3 bg-slate-800 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                        {notif.notification_type === "patrolActivity" ? (
                          <Car className="w-4 h-4 text-white" />
                        ) : notif.notification_type === "masterNotification" ? (
                          <Send className="w-4 h-4 text-white" />
                        ) : notif.notification_type === "perpetratorUpdates" ? (
                          <User className="w-4 h-4 text-white" />
                        ) : notif.has_1085 || notif.has_1013 ? (
                          <AlertTriangle className="w-4 h-4 text-white" />
                        ) : (
                          <Bell className="w-4 h-4 text-white" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{notif.title}</span>
                          <Badge className={`${typeInfo.color} text-xs`}>
                            {typeInfo.label}
                          </Badge>
                          {notif.is_priority && (
                            <Badge className="bg-amber-600 text-xs">Priority</Badge>
                          )}
                          {notif.has_1085 && (
                            <Badge className="bg-orange-600 text-xs">10-85</Badge>
                          )}
                          {notif.has_1013 && (
                            <Badge className="bg-red-600 text-xs">10-13</Badge>
                          )}
                          {notif.is_update && (
                            <Badge variant="outline" className="text-xs">Update</Badge>
                          )}
                        </div>

                        <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap">
                          {notif.message}
                        </p>

                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                          <span>{new Date(notif.received_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</span>
                          {notif.call_number && (
                            <span>Call: {notif.call_number}</span>
                          )}
                          {notif.location && (
                            <span className="truncate max-w-[200px]">{notif.location}</span>
                          )}
                          {notif.patrol_car_number && (
                            <span>Car: {notif.patrol_car_number}</span>
                          )}
                          {notif.sent_by_name && notif.notification_type !== "masterNotification" && (
                            <span>From: {notif.sent_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
              <span className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1 || loading}
                  onClick={() => loadNotifications(page - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages || loading}
                  onClick={() => loadNotifications(page + 1)}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
